import React, { useEffect, useRef, useState } from 'react';
import styles from './ProductDetailsZoomSlider.module.scss';
import classnames from 'classnames';
import { withBreakpoints } from 'react-breakpoints';
import Hammer from 'react-hammerjs';
import Slider, { Settings } from 'react-slick';
import { AssetEntity } from 'src/modules/assets';
import { useWindowSize } from 'src/utils/window';
import { LayoutOverlay } from 'src/modules/layout';
import { GlobalIcon, IconImage } from 'src/modules/global';
import { useIntl } from 'react-intl';
import { classes } from 'src/utils/styles';
import ScrollBound from 'react-scroll-bound';
import { isLandscapeAsset } from 'src/modules/products';
import { breakpoints } from 'src/utils/breakpoints';

export interface ProductDetailsZoomSliderProps {
  activeAsset: SliderAssetEntity;
  assets: SliderAssetEntity[];
  onClose: () => void;
  caption?: string;
  fullScreen?: boolean;
  currentBreakpoint: string;
  objectFit?: 'cover' | 'contain';
}

export interface SliderAssetEntity extends AssetEntity {
  name?: string;
}

const ProductDetailsZoomSlider: React.FunctionComponent<ProductDetailsZoomSliderProps> = (
  props
) => {
  const intl = useIntl();
  const {
    activeAsset: initialActiveAsset,
    assets,
    onClose,
    caption,
    fullScreen = false,
    currentBreakpoint,
    objectFit = 'cover',
  } = props;

  const findAssetIndex = () => {
    return activeAsset ? assets.findIndex((a) => a.id === activeAsset.id) : 0;
  };

  const [activeAsset, setActiveAsset] = useState(initialActiveAsset);
  const [currentIndex, setCurrentIndex] = useState(findAssetIndex());
  const [isBlocked, setIsBlocked] = useState(false);
  const [zoomContainerOpen, setZoomContainerOpen] = useState(false);
  const [zoomedImageStylePosition, setZoomedImageStylePosition] = useState({ top: 0, left: 0 });
  const [panStartPosition, setPanStartPosition] = useState({ top: 0, left: 0 });
  const [zoomedImageStyleDimensions, setZoomedImageStyleDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [zoomStartDimensionsAndPosition, setZoomStartDimensionsAndPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  });
  const [pinchStarted, setPinchStarted] = useState(false);

  const [hack, setHack] = useState('');

  const slider = useRef<Slider>();

  const { width } = useWindowSize();

  const speed = 500;

  const settings: Settings = {
    dots: false,
    arrows: false,
    infinite: true,
    centerMode: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    centerPadding: '0px',
    // variableWidth: false,
    adaptiveHeight: false,
    speed,
    swipe: false,
    afterChange: (newIndex: number) => {
      setActiveAsset(assets[newIndex]);
      setCurrentIndex(newIndex);
    },
  };

  useEffect(() => {
    setHack('hack');
  }, []);

  useEffect(() => {
    // target width / height is minimum 2x window width or 2x window height)
    const sizeProportion = 2;

    let targetWidth;
    let targetHeight;

    if (isLandscapeAsset(activeAsset)) {
      targetHeight = sizeProportion * window.innerHeight;
      targetWidth = activeAsset.file?.width / (activeAsset.file?.height / targetHeight);
    } else {
      targetWidth = sizeProportion * window.innerWidth;
      targetHeight = activeAsset.file?.height / (activeAsset.file?.width / targetWidth);
    }

    setZoomedImageStyleDimensions({
      width: targetWidth,
      height: targetHeight,
    });
  }, [activeAsset]);

  const unblock = () => {
    setIsBlocked(true);
    setTimeout(() => {
      setIsBlocked(false);
    }, speed);
  };

  const handleNext = () => {
    if (isBlocked) {
      return;
    }
    unblock();

    slider.current.slickNext();
  };
  const handlePrev = () => {
    if (isBlocked) {
      return;
    }
    unblock();

    slider.current.slickPrev();
  };

  const setSliderRef = (s) => {
    slider.current = s;

    if (s) {
      s.slickGoTo(currentIndex);
    }
  };

  const closeOverlay = () => {
    document.body.style.overflow = 'unset';
    onClose();
  };

  const zoomToCoordinates = (x, y) => {
    const leftProportion = x / window.innerWidth;
    const topProportion = y / window.innerHeight;

    setZoomedImageStylePosition({
      left: leftProportion * (window.innerWidth - zoomedImageStyleDimensions.width),
      top: topProportion * (window.innerHeight - zoomedImageStyleDimensions.height),
    });
  };

  const handleMouseMove = (e) => {
    zoomToCoordinates(e.clientX, e.clientY);
  };

  const openZoomContainer = (e) => {
    zoomToCoordinates(e.clientX, e.clientY);
    setZoomContainerOpen(true);
  };

  const closeZoomContainer = () => {
    setZoomContainerOpen(false);
  };

  const onPanStart = (e) => {
    setPanStartPosition(zoomedImageStylePosition);
    e.srcEvent.preventDefault();
    e.srcEvent.stopPropagation();
  };

  const updatePosition = ({ left, top }: { left: number; top: number }) => {
    setZoomedImageStylePosition({
      top: Math.max(window.innerHeight - zoomedImageStyleDimensions.height, Math.min(0, top)),
      left: Math.max(window.innerWidth - zoomedImageStyleDimensions.width, Math.min(0, left)),
    });
  };

  const onPan = (e) => {
    if (pinchStarted) {
      return;
    }

    updatePosition({
      top: panStartPosition.top + e.deltaY,
      left: panStartPosition.left + e.deltaX,
    });
    e.srcEvent.preventDefault();
    e.srcEvent.stopPropagation();
  };

  const onPinchStart = (e) => {
    setPinchStarted(true);
    setZoomStartDimensionsAndPosition({
      ...zoomedImageStyleDimensions,
      ...zoomedImageStylePosition,
    });
    e.srcEvent.preventDefault();
    e.srcEvent.stopPropagation();
  };

  const onPinch = (e) => {
    const targetWidth = zoomStartDimensionsAndPosition.width * e.scale;
    const targetHeight = zoomStartDimensionsAndPosition.height * e.scale;

    if (
      targetWidth > window.innerWidth &&
      targetHeight > window.innerHeight &&
      targetWidth < window.innerWidth * 10 &&
      targetHeight < window.innerHeight * 10
    ) {
      setZoomedImageStyleDimensions({
        width: targetWidth,
        height: targetHeight,
      });

      updatePosition({
        left:
          zoomStartDimensionsAndPosition.left -
          (targetWidth - zoomStartDimensionsAndPosition.width) / 2,
        top:
          zoomStartDimensionsAndPosition.top -
          (targetHeight - zoomStartDimensionsAndPosition.height) / 2,
      });
    }
    e.srcEvent.preventDefault();
    e.srcEvent.stopPropagation();
  };

  const onPinchEnd = () => {
    setTimeout(() => {
      setPinchStarted(false);
    }, 200);
  };

  return (
    <LayoutOverlay
      key={`zoomslider-${currentBreakpoint}-${hack}`}
      display={!!activeAsset}
      onClose={onClose}
      showClose={false}
      className={styles.slider}>
      <ScrollBound
        className={classnames(styles.wrapper, fullScreen && styles.fullScreen)}
        tagName="div">
        {breakpoints[currentBreakpoint] < breakpoints.md ? (
          <Hammer
            onPan={onPan}
            onPanStart={onPanStart}
            onPinchStart={onPinchStart}
            onPinch={onPinch}
            onPinchEnd={onPinchEnd}
            options={{
              domEvents: true,
              recognizers: {
                pinch: { enable: true },
              },
            }}>
            <div
              className={classnames(
                styles.zoomContainer,
                zoomContainerOpen && styles.zoomContainerVisible
              )}>
              <GlobalIcon
                image={IconImage.Close}
                size="custom"
                className={styles.close}
                onClick={closeZoomContainer}
              />
              <img
                src={activeAsset.file?.url}
                style={{
                  ...zoomedImageStylePosition,
                  ...zoomedImageStyleDimensions,
                }}
                className={styles.zoomedImage}
              />
            </div>
          </Hammer>
        ) : (
          <div
            className={classnames(
              styles.zoomContainer,
              zoomContainerOpen && styles.zoomContainerVisible
            )}
            onMouseMove={handleMouseMove}
            onClick={closeZoomContainer}>
            <img
              src={activeAsset.file?.url}
              style={{
                ...zoomedImageStylePosition,
                ...zoomedImageStyleDimensions,
              }}
              className={styles.zoomedImage}
            />
          </div>
        )}
        <GlobalIcon
          image={IconImage.Close}
          size="custom"
          className={styles.close}
          onClick={closeOverlay}
        />
        <div className={classes([styles.content, fullScreen ? styles.fullScreen : ''])}>
          <div className={classes(styles.sliderWrapper)}>
            <Slider ref={setSliderRef} {...settings} initialSlide={currentIndex}>
              {assets.map((a, i) => (
                <div
                  key={a.id}
                  data-index={i}
                  className={classes([
                    styles.slideWrapper,
                    a.id === (activeAsset && activeAsset.id) ? styles.active : null,
                  ])}
                  style={{ width }}>
                  <div
                    className={styles.imageWrapper}
                    onClick={fullScreen ? openZoomContainer : onClose}>
                    {a.file && (
                      <img
                        src={a.file?.url}
                        className={
                          a.file?.width / a.file?.height > window.innerWidth / window.innerHeight
                            ? objectFit === 'contain'
                              ? styles.fitWidth
                              : styles.fitHeight
                            : objectFit === 'contain'
                            ? styles.fitHeight
                            : styles.fitWidth
                        }
                      />
                    )}
                  </div>
                </div>
              ))}
            </Slider>
            <div className={styles.footer}>
              <div>
                <h4>
                  {caption}{' '}
                  {activeAsset && activeAsset.name && (
                    <>
                      <GlobalIcon image={IconImage.Minus} />
                      {activeAsset.name}
                    </>
                  )}
                </h4>
              </div>
              <div>
                <GlobalIcon
                  image={IconImage.LeftArrow}
                  onClick={handlePrev}
                  className={styles.footerIcon}
                />
                <span className={styles.count}>{`${currentIndex + 1} ${intl.formatMessage({
                  id: 'page/professionals/downloads/products/detail/slider_of',
                })} ${assets.length}`}</span>
                <GlobalIcon
                  image={IconImage.RightArrow}
                  onClick={handleNext}
                  className={styles.footerIcon}
                />
              </div>
            </div>
          </div>
        </div>
      </ScrollBound>
    </LayoutOverlay>
  );
};

export default withBreakpoints(ProductDetailsZoomSlider);
